import { parse, visit, Kind } from "graphql";
import type { DocumentNode, FieldNode } from "graphql";

const MAX_DEPTH = 5;
const MAX_FIELDS = 50;

interface ValidationResult {
  valid: boolean;
  error?: string;
}

export class AppApiProxy {
  private readonly grantedPermissions: string[];

  constructor(grantedPermissions: string[]) {
    this.grantedPermissions = grantedPermissions;
  }

  validateAndProxy(document: string, variables?: string): ValidationResult {
    // 1. Parse the GraphQL document
    let ast: DocumentNode;
    try {
      ast = parse(document);
    } catch (err) {
      return {
        valid: false,
        error: `GraphQL parse error: ${err instanceof Error ? err.message : String(err)}`,
      };
    }

    // 2. Validate variables JSON if provided
    if (variables) {
      try {
        JSON.parse(variables);
      } catch {
        return { valid: false, error: "Variables is not valid JSON" };
      }
    }

    // 3. Block introspection queries
    const introspectionResult = this.checkIntrospection(ast);
    if (introspectionResult) {
      return introspectionResult;
    }

    // 4. Enforce depth limit
    const depthResult = this.checkDepth(ast);
    if (depthResult) {
      return depthResult;
    }

    // 5. Enforce field count limit
    const fieldCountResult = this.checkFieldCount(ast);
    if (fieldCountResult) {
      return fieldCountResult;
    }

    // Permission enforcement is handled server-side by OauthGuard's
    // permission ceiling filtering — no client-side scope map needed.

    return { valid: true };
  }

  private checkIntrospection(ast: DocumentNode): ValidationResult | null {
    let introspectionFound = false;

    visit(ast, {
      Field(node: FieldNode) {
        if (node.name.value === "__schema" || node.name.value === "__type") {
          introspectionFound = true;
        }
      },
    });

    if (introspectionFound) {
      return { valid: false, error: "Introspection queries are not allowed" };
    }

    return null;
  }

  private checkDepth(ast: DocumentNode): ValidationResult | null {
    let maxObservedDepth = 0;

    const measureDepth = (
      selections: readonly {
        readonly kind: string;
        readonly selectionSet?: { readonly selections: readonly any[] };
      }[],
      currentDepth: number,
    ): void => {
      for (const selection of selections) {
        if (selection.kind === Kind.FIELD) {
          if (currentDepth > maxObservedDepth) {
            maxObservedDepth = currentDepth;
          }
          if (selection.selectionSet) {
            measureDepth(selection.selectionSet.selections, currentDepth + 1);
          }
        } else if (
          selection.kind === Kind.INLINE_FRAGMENT ||
          selection.kind === Kind.FRAGMENT_SPREAD
        ) {
          if (
            (selection as { selectionSet?: { selections: readonly any[] } })
              .selectionSet
          ) {
            measureDepth(
              (selection as { selectionSet: { selections: readonly any[] } })
                .selectionSet.selections,
              currentDepth,
            );
          }
        }
      }
    };

    visit(ast, {
      OperationDefinition(node) {
        if (node.selectionSet) {
          measureDepth(node.selectionSet.selections, 1);
        }
      },
    });

    if (maxObservedDepth > MAX_DEPTH) {
      return {
        valid: false,
        error: `Query depth ${maxObservedDepth} exceeds maximum allowed depth of ${MAX_DEPTH}`,
      };
    }

    return null;
  }

  private checkFieldCount(ast: DocumentNode): ValidationResult | null {
    let fieldCount = 0;

    visit(ast, {
      Field() {
        fieldCount += 1;
      },
    });

    if (fieldCount > MAX_FIELDS) {
      return {
        valid: false,
        error: `Query contains ${fieldCount} fields, exceeding maximum of ${MAX_FIELDS}`,
      };
    }

    return null;
  }
}
